/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  Box,
  Button,
  createTheme,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  Icon,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  TextField,
  ThemeProvider,
  Tooltip,
  Typography,
  Select,
} from '@mui/material';
import { DataGrid, GridCellParams, GridColDef } from '@mui/x-data-grid';
import { esES, ptBR } from '@mui/material/locale';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { BasePage, Container } from '../../commons/styleds/styleds';

import React from 'react';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import { GridTopPage, IconTituloStato } from '../../commons/styleds/styleds';
import { useTranslation } from '../../commons/useTranslasion';
import { IVagaExterna } from '../../commons/types/vagaExterna';
import { useVagaExternaActions, IVagasExternasFilter, DataPublicacaoRange, ModeloTrabalho, Origem } from '../../states/vagasExternas/vagaExterna.actions';
import styled from 'styled-components';
import { useRecoilValue } from 'recoil';
import { authAtom } from '../../states/usuarios/AuthState';

interface ColumnProps {
  handleOpenDialog: (obj: IVagaExterna, event?: React.MouseEvent) => void;
}

const VagasExternasComponent: React.FC = () => {
  const { translate, language } = useTranslation();
  const lang = language ?? 'pt-br';
  let locale = ptBR;
  switch (lang.slice(0, 2)) {
    case 'es':
      locale = esES
      break;
  }

  const auth = useRecoilValue(authAtom);
  const actions = useVagaExternaActions();
  const [loading, setLoading] = useState<boolean>(true);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [selectedVaga, setSelectedVaga] = useState<IVagaExterna | null>(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [rows, setRows] = useState<IVagaExterna[]>([]);
  const [rowCount, setRowCount] = useState<number>(0);
  const [filterValues, setFilterValues] = useState<IVagasExternasFilter>({
    origem: undefined,
    titulo: '',
    area: '',
    faixaSalarial: '',
    localidade: '',
    dataPublicacao: undefined,
    modeloTrabalho: undefined,
    senioridade: ''
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const filterParams: IVagasExternasFilter = {
        ...filterValues,
        pagina: paginationModel.page + 1,
        tamanhoPagina: paginationModel.pageSize,
      };
      const res = await actions.filter(filterParams, false);
      setRows(res.itens);
      setRowCount(res.totalItens);
    } catch (error) {
      toast.error(translate('erroAoBuscarVagas'));
      console.error('Erro ao buscar vagas:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [paginationModel.page, paginationModel.pageSize]);

  const handleFilterChange = <T extends keyof IVagasExternasFilter>
    (field: T, value: IVagasExternasFilter[T]) => {
    setFilterValues((prev: any) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleApplyFilters = () => {
    setPaginationModel(prev => ({
      ...prev,
      page: 0
    }));
    loadData();
  };

  const handleClearFilters = () => {
    setFilterValues({
      titulo: '',
      area: '',
      faixaSalarial: '',
      localidade: '',
      dataPublicacao: undefined,
      modeloTrabalho: undefined,
      senioridade: '',
      origem: undefined,
    });
    setPaginationModel(prev => ({
      ...prev,
      page: 0
    }));
    loadData();
  };

  const handleOpenDialog = (obj: IVagaExterna, event?: React.MouseEvent) => {
    if (event) event.stopPropagation();
    setSelectedVaga(obj);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedVaga(null);
  };

  const createColumns = ({
    handleOpenDialog,
  }: ColumnProps): GridColDef<IVagaExterna>[] => [
      {
        field: 'id',
        headerName: translate('idVaga'),
        width: 80,
        renderCell: (params: GridCellParams) => {
          const obj = params.row;
          return `INTOO-ATS-${obj.idExterno}`;
        },
      },
      {
        field: 'titulo',
        headerName: translate('tituloVaga'),
        width: 180,
      },
      {
        field: 'cidade',
        headerName: translate('cidade'),
        width: 120,
      },
      {
        field: 'estado',
        headerName: translate('estado'),
        width: 120,
      },
      {
        field: 'pais',
        headerName: translate('paisVaga'),
        width: 120,
      },
      {
        field: 'dataPublicacao',
        headerName: translate('dataPublicacao'),
        width: 120,
        renderCell: (params: GridCellParams) => {
          const obj = params.row;
          return obj.dataPublicacao ? new Date(obj.dataPublicacao).toLocaleDateString() : '-';
        },
      },
      {
        field: 'action',
        headerName: translate('acoes'),
        width: 100,
        editable: true,
        renderCell: (params: GridCellParams) => {
          const obj = params.row as IVagaExterna;
          return (
            <>
              <Icon
                sx={{ cursor: 'pointer' }}
                onClick={(e) => handleOpenDialog(obj, e)}
              >
                <EditIcon />
              </Icon>
            </>
          );
        },
      },
    ];

  const columns = createColumns({ handleOpenDialog });

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString();
  };

  return (
    <BasePage>
      <GridTopPage item xs={12} md={12}>
        <Grid xs={9} md={6}>
          <TituloPagina>
            {translate('vagasExternas')}
          </TituloPagina>
        </Grid>
      </GridTopPage>

      <Container container>
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: '15px' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                <FilterListIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                {translate('filtros')}
              </Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={translate('tituloVaga')}
                  value={filterValues.titulo}
                  onChange={(e) => handleFilterChange('titulo', e.target.value)}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={translate('areaVaga')}
                  value={filterValues.area}
                  onChange={(e) => handleFilterChange('area', e.target.value)}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={translate('localidade')}
                  value={filterValues.localidade}
                  onChange={(e) => handleFilterChange('localidade', e.target.value)}
                  variant="outlined"
                  size="small"
                  placeholder="Cidade/Estado"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={translate('faixaSalarial')}
                  value={filterValues.faixaSalarial}
                  onChange={(e) => handleFilterChange('faixaSalarial', e.target.value)}
                  variant="outlined"
                  size="small"
                  placeholder="Ex: 3000-5000"
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>{translate('dataPublicacao')}</InputLabel>
                  <Select
                    value={filterValues.dataPublicacao || ''}
                    onChange={(e) => handleFilterChange(
                      'dataPublicacao',
                      e.target.value as DataPublicacaoRange
                    )}
                    label={translate('dataPublicacao')}
                  >
                    <MenuItem value="">{translate('todosOpcao')}</MenuItem>
                    <MenuItem value={DataPublicacaoRange.Ultimos7Dias}>
                      {translate('ultimos7Dias')}
                    </MenuItem>
                    <MenuItem value={DataPublicacaoRange.Ultimos15Dias}>
                      {translate('ultimos15Dias')}
                    </MenuItem>
                    <MenuItem value={DataPublicacaoRange.Ultimos30Dias}>
                      {translate('ultimos30Dias')}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>{translate('modeloTrabalho')}</InputLabel>
                  <Select
                    value={filterValues.modeloTrabalho || ''}
                    onChange={(e) => handleFilterChange(
                      'modeloTrabalho',
                      e.target.value as ModeloTrabalho
                    )}
                    label={translate('modeloTrabalho')}
                  >
                    <MenuItem value="">{translate('todosOpcao')}</MenuItem>
                    <MenuItem value={ModeloTrabalho.Presencial}>{translate('presencial')}</MenuItem>
                    <MenuItem value={ModeloTrabalho.Hibrido}>{translate('hibrido')}</MenuItem>
                    <MenuItem value={ModeloTrabalho.Remoto}>{translate('remoto')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={translate('senioridade')}
                  value={filterValues.senioridade}
                  onChange={(e) => handleFilterChange('senioridade', e.target.value)}
                  variant="outlined"
                  size="small"
                  placeholder="Ex: Sênior, Pleno"
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>{translate('origem')}</InputLabel>
                  <Select
                    value={filterValues.origem || ''}
                    onChange={(e) => handleFilterChange('origem', e.target.value as Origem)}
                    label={translate('origem')}
                  >
                    <MenuItem value="">{translate('todosOpcao')}</MenuItem>
                    {Object.values(Origem).map((value) => (
                      <MenuItem key={value} value={value}>
                        {translate(value)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={3}>
                <Box display="flex" gap={1} height="100%" alignItems="center">
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleClearFilters}
                    startIcon={<ClearIcon />}
                    size="medium"
                    sx={{ flexGrow: 1 }}
                  >
                    {translate('limpar')}
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleApplyFilters}
                    startIcon={<SearchIcon />}
                    size="medium"
                    sx={{ flexGrow: 1 }}
                  >
                    {translate('pesquisar')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          <Box
            sx={{
              height: '60vh',
              width: '100%',
              marginTop: '20px',
            }}
          >
            <ThemeProvider theme={createTheme({}, locale)}>
              <DataGrid
                getRowId={(row) => row?.id || 0}
                rows={rows}
                columns={columns}
                pagination
                pageSizeOptions={[10, 25, 50, 75, 100]}
                rowCount={rowCount}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                paginationMode="server"
                loading={loading}
                sx={{
                  backgroundColor: 'white',
                  border: '1px solid black',
                  borderRadius: '20px',
                }} />
            </ThemeProvider>
          </Box>
        </Grid>
      </Container>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        {(openDialog && selectedVaga) && (
          <>
            <DialogTitle>
              <Typography variant="h6" fontWeight="bold">
                {selectedVaga.titulo || translate('semTitulo')}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                {selectedVaga.empresa || translate('empresaNaoInformada')}
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper elevation={0} sx={{ p: 2, height: '100%' }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      {translate('informacoesGerais')}
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          {translate('origem')}:
                        </Typography>
                        <Typography variant="body1">
                          {selectedVaga.origem || '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          {translate('fonte')}:
                        </Typography>
                        <Typography variant="body1">
                          {selectedVaga.fonte || '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          {translate('dataPublicacao')}:
                        </Typography>
                        <Typography variant="body1">
                          {formatDate(selectedVaga.dataPublicacao)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          {translate('dataCaptura')}:
                        </Typography>
                        <Typography variant="body1">
                          {formatDate(selectedVaga.dataCaptura)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          {translate('localizacao')}:
                        </Typography>
                        <Typography variant="body1">
                          {[selectedVaga.cidade, selectedVaga.estado, selectedVaga.pais]
                            .filter(Boolean)
                            .join(', ') || '-'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper elevation={0} sx={{ p: 2, height: '100%' }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      {translate('informacoesProfissionais')}
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          {translate('areaVaga')}:
                        </Typography>
                        <Typography variant="body1">
                          {selectedVaga.area || '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          {translate('subArea')}:
                        </Typography>
                        <Typography variant="body1">
                          {selectedVaga.subArea || '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          {translate('nivelCargo')}:
                        </Typography>
                        <Typography variant="body1">
                          {selectedVaga.nivelCargo || '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          {translate('modalidadeTrabalho')}:
                        </Typography>
                        <Typography variant="body1">
                          {selectedVaga.modalidadeTrabalho || '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          {translate('regimeContratacao')}:
                        </Typography>
                        <Typography variant="body1">
                          {selectedVaga.regimeContratacao || '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          {translate('remuneracao')}:
                        </Typography>
                        <Typography variant="body1">
                          {selectedVaga.remuneracao || selectedVaga.faixaSalarial || '-'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <Paper elevation={0} sx={{ p: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      {translate('descricaoVaga')}
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                      {selectedVaga.descricao || translate('semDescricao')}
                    </Typography>
                  </Paper>
                </Grid>

                {selectedVaga.requisitos && (
                  <Grid item xs={12}>
                    <Paper elevation={0} sx={{ p: 2 }}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        {translate('requisitos')}
                      </Typography>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                        {selectedVaga.requisitos}
                      </Typography>
                    </Paper>
                  </Grid>
                )}

                {selectedVaga.beneficios && (
                  <Grid item xs={12}>
                    <Paper elevation={0} sx={{ p: 2 }}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        {translate('beneficios')}
                      </Typography>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                        {selectedVaga.beneficios}
                      </Typography>
                    </Paper>
                  </Grid>
                )}

                {selectedVaga.url && (
                  <Grid item xs={12}>
                    <Paper elevation={0} sx={{ p: 2 }}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        {translate('linkOriginal')}
                      </Typography>
                      <Typography
                        onClick={() => actions.notificarAcao(
                          auth?.id!, 
                          selectedVaga.id!
                        )}
                        variant="body1"
                        component="a"
                        href={selectedVaga.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {selectedVaga.url}
                      </Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>{translate('fecharDialog')}</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </BasePage>
  );
};

const TituloPagina = styled(Grid)`
font-family: Roboto Slab !important;
font-size: 60px !important;
font-weight: 300 !important;
line-height: 72px !important;
letter-spacing: -0.03em !important;
text-align: left !important;
text-underline-position: from-font !important;
text-decoration-skip-ink: none !important;

background: linear-gradient(90deg, #118BD7 0%, #75B21D 100%);
-webkit-background-clip: text;
-webkit-text-fill-color: transparent;
`

export default VagasExternasComponent;
